import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const categoryLabelMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  jilupian: '纪录片',
  duanpian: '短片',
  dongman: '动漫',
  zongyi: '综艺',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const video = videos.find((v) => v.id === id);
  const article = articles.find((a) => a.id === id);
  const item = video || article;

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">内容未找到</h1>
            <p className="text-sm text-foreground-500 mt-3">该内容不存在或已被移除</p>
            <Link to="/" className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const isVideo = 'duration' in item;
  const categorySlug = item.category;
  const categoryName = categoryLabelMap[categorySlug] || categorySlug;
  const relatedVideos = videos
    .filter((v) => v.category === item.category && v.id !== id)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title,
    description: 'excerpt' in item ? item.excerpt : item.description,
    image: 'thumbnail' in item ? item.thumbnail : item.image,
    datePublished: 'date' in item ? item.date : `${item.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: 'author' in item ? item.author : '亚洲精品视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '亚洲精品视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://beevpn.cn/article/${id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName, href: `/category/${categorySlug}` },
                  { label: item.title },
                ]}
              />
            </div>

            <header className="mb-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${categorySlug}`}
                  className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors"
                >
                  {categoryName}
                </Link>
                {'tags' in item && item.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-500 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                {item.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                {'author' in item && <span>{item.author}</span>}
                {'date' in item && <span>{item.date}</span>}
                {'year' in item && <span>{item.year}</span>}
                {'views' in item && <span>{item.views} 次浏览</span>}
                {'readTime' in item && <span>{item.readTime}</span>}
                {'rating' in item && (
                  <span className="flex items-center gap-0.5 text-accent-500 font-medium">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-xs"></i>
                    </span>
                    {item.rating}
                  </span>
                )}
              </div>
            </header>

            {'thumbnail' in item && (
              <div className="w-full aspect-video rounded-lg overflow-hidden mb-10">
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}

            {'image' in item && (
              <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-10">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            )}

            {'content' in item ? (
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: item.content }}
              />
            ) : (
              <div className="text-foreground-700 leading-relaxed">
                <p className="text-base mb-6">{item.description}</p>
                {'duration' in item && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6 bg-background-100 rounded-lg mb-6">
                    <div>
                      <span className="text-xs text-foreground-400">时长</span>
                      <p className="font-medium text-foreground-900 text-sm mt-0.5">{item.duration}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">年份</span>
                      <p className="font-medium text-foreground-900 text-sm mt-0.5">{item.year}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">地区</span>
                      <p className="font-medium text-foreground-900 text-sm mt-0.5">{item.region}</p>
                    </div>
                    <div>
                      <span className="text-xs text-foreground-400">播放量</span>
                      <p className="font-medium text-foreground-900 text-sm mt-0.5">{item.views}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {'tags' in item && (
              <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
                {item.tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-md">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </article>

          {relatedVideos.length > 0 && (
            <section className="max-w-7xl mx-auto px-4 md:px-6 mt-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((v) => (
                  <Link
                    key={v.id}
                    to={`/article/${v.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-foreground-950/70 text-background-50 text-[10px] rounded">
                        {v.duration}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors text-sm truncate">
                        {v.title}
                      </h3>
                      <div className="flex items-center justify-between mt-2 text-xs text-foreground-400">
                        <span>{v.year} · {v.region}</span>
                        <span className="flex items-center gap-0.5 text-accent-500">
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-star-fill text-[10px]"></i>
                          </span>
                          {v.rating}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}